/**
 * @file 定缺选择策略 —— `design/gdd/06-ai-strategy.md` §2。
 *
 * 定缺是整局唯一一次「一锤子买卖」的决策，选错直接导致后面十几巡都在拆牌。
 * 对三门花色分别打分，**分数越低越适合作缺**。
 *
 * 关键设计（§2.2）：**孤张权重为负**。一门 6 张全是孤张（如 `1m 3m 5m 7m 9m` 散落）
 * 比一门 4 张全是连张的更适合作缺 —— 新手 AI 只看张数会选错，加入孤张负权后
 * 行为立刻变得像老手。
 *
 * 纯函数、平台无关。
 */

import type { AIDifficulty } from '../config';
import type { Suit } from '../types';
import { W_COUNT, W_TRIPLET, W_PAIR, W_CONN, W_GAP, W_ISOLATED } from './params';
import type { TileCounts } from './types';

/**
 * 对某门花色的定缺评分（§2.1）。分数越低越适合作缺。
 *
 * 难度差异（§2.3）：
 * - 简单：**只用 `n`**（张数最少的一门），忽略所有结构项；
 * - 普通：完整评估函数，不含番型潜力项；
 * - 困难：完整评估函数 + 番型潜力项（另外两门有一门 ≥ 8 张 → 保留清一色可能性，本门更适合作缺）。
 *
 * @param counts    手牌计数向量
 * @param suitIndex 花色序号（0=wan 1=tong 2=tiao）
 */
export function evaluateQue(counts: TileCounts, suitIndex: number, difficulty: AIDifficulty): number {
  const base = suitIndex * 9;

  // 简单档：只用张数
  if (difficulty === 'EASY') {
    let n = 0;
    for (let r = 0; r < 9; r++) n += counts[base + r] ?? 0;
    return n * W_COUNT;
  }

  let n = 0;
  let triplets = 0;
  let pairs = 0;
  let conn = 0;
  let gap = 0;
  for (let r = 0; r < 9; r++) {
    const c = counts[base + r] ?? 0;
    n += c;
    if (c >= 3) triplets++;
    if (c === 2) pairs++;
    if (r <= 7 && c > 0 && (counts[base + r + 1] ?? 0) > 0) conn++; // 两面/边张搭子
    if (r <= 6 && c > 0 && (counts[base + r + 1] ?? 0) === 0 && (counts[base + r + 2] ?? 0) > 0) {
      gap++; // 坎张搭子
    }
  }

  // 孤张：count === 1 且左右两格（±2）内无其它同花色牌
  let isolated = 0;
  for (let r = 0; r < 9; r++) {
    if ((counts[base + r] ?? 0) !== 1) continue;
    let near = false;
    for (let d = -2; d <= 2; d++) {
      if (d === 0) continue;
      const rr = r + d;
      if (rr < 0 || rr > 8) continue;
      if ((counts[base + rr] ?? 0) > 0) {
        near = true;
        break;
      }
    }
    if (!near) isolated++;
  }

  let score =
    n * W_COUNT +
    triplets * W_TRIPLET +
    pairs * W_PAIR +
    conn * W_CONN +
    gap * W_GAP -
    isolated * W_ISOLATED;

  // 困难档额外考虑番型潜力：若另外两门中有一门张数极多 → 保留清一色可能性，本门更适合作缺
  if (difficulty === 'HARD') {
    let otherMax = 0;
    for (let s = 0; s < 3; s++) {
      if (s === suitIndex) continue;
      let cnt = 0;
      for (let r = 0; r < 9; r++) cnt += counts[s * 9 + r] ?? 0;
      if (cnt > otherMax) otherMax = cnt;
    }
    if (otherMax >= 8) score -= 40;
  }
  return score;
}

/**
 * 选定缺花色（§2.1）。对三门评分取 `argmin`；平分时取 suit 值最小者，保证确定性。
 *
 * @param counts 手牌计数向量
 * @returns 定缺花色 `'wan' | 'tong' | 'tiao'`
 */
export function pickQue(counts: TileCounts, difficulty: AIDifficulty): Suit {
  let bestSuit = 0;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let s = 0; s < 3; s++) {
    const score = evaluateQue(counts, s, difficulty);
    if (score < bestScore) {
      bestScore = score;
      bestSuit = s;
    }
  }
  return (['wan', 'tong', 'tiao'] as const)[bestSuit]!;
}
